import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area,
} from 'recharts';
import {
  inventoryUrl, inventoryConsolidatedUrl, inventoryAdjustUrl, inventoryAnalyticsUrl,
  productHistoryUrl, openingStockUrl, branchesUrl, ASSETS,
  inventoryAdjustmentsUrl, inventoryAdjustmentApproveUrl,
  inventoryAdjustmentRejectUrl, inventoryAdjustmentCancelUrl,
} from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtMoney, fmtNumber, fmtDate } from '../../utils/format';
import { toastSuccess, showAxiosError, swalWarning, swalDangerConfirm, swalConfirm } from '../../utils/swal';
import { defaultImageFor } from '../../utils/productImage';

const REASONS = [
  { key: 'adjustment', label: 'Manual adjustment' },
  { key: 'damage', label: 'Damaged stock' },
  { key: 'theft', label: 'Theft / loss' },
  { key: 'count_correction', label: 'Stock-count correction' },
  { key: 'returned', label: 'Returned to supplier' },
];

const QUICK_DELTAS = [-10, -5, -1, +1, +5, +10, +50];

// Same fallback rule used by the Products page so a row without an image
// shows the right default art per unit / category.
const inventoryImageSrc = (r) => `${ASSETS}${r.image || defaultImageFor(r)}`;

const Inventory = () => {
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const isSuper = me?.role === 'super_admin';
  const isManager = me?.role === 'branch_manager';
  // Only the branch manager and the super admin can touch inventory adjustments.
  // Regular staff (cashier, store_officer, accountant, etc.) get read-only.
  const canRequestAdjustment = isSuper || isManager;

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(me?.branch_id || '');
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | low | oos
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [adjust, setAdjust] = useState(null);
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('adjustment');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ─── Opening stock loader ────────────────────────────────
  const [openOpening, setOpenOpening] = useState(false);
  const [openingQtys, setOpeningQtys] = useState({}); // { productId: { qty, cost } }
  const [openingSearch, setOpeningSearch] = useState('');
  const [openingNote, setOpeningNote] = useState('');
  const [openingSaving, setOpeningSaving] = useState(false);
  // Off by default → the guardrail protects real stock. Turn on if the branch
  // was seeded with sample/starter stock and you want to replace it with the
  // real physical count.
  const [openingReplace, setOpeningReplace] = useState(false);

  const [tab, setTab] = useState('stock'); // 'stock' | 'analytics' | 'approvals'

  // ─── Approval queue (branch manager sees their own; super admin sees all) ─
  const [adjustments, setAdjustments] = useState([]);
  const [adjLoading, setAdjLoading] = useState(false);
  const [adjStatus, setAdjStatus] = useState('pending'); // pending | approved | rejected | all
  const [pendingCount, setPendingCount] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [history, setHistory] = useState(null); // { product, movements, totals }
  const [historyLoading, setHistoryLoading] = useState(false);

  // ─── Consolidated (all-branches) view — super admin only ─────────────
  // When active, we render a company-wide table instead of one branch.
  const [combined, setCombined] = useState(false);
  const [combinedRows, setCombinedRows] = useState([]);
  const [combinedBranches, setCombinedBranches] = useState([]);
  const [combinedLoading, setCombinedLoading] = useState(false);
  const [combinedError, setCombinedError] = useState(null);

  const loadCombined = async () => {
    setCombinedLoading(true);
    setCombinedError(null);
    try {
      const res = await axios.get(inventoryConsolidatedUrl);
      if (res.data?.success) {
        setCombinedRows(res.data.results || []);
        setCombinedBranches(res.data.branches || []);
      } else {
        setCombinedError(res.data?.message || 'Failed to load combined view');
      }
    } catch (err) {
      setCombinedError(err.response?.data?.message || err.message || 'Failed to load combined view');
    } finally { setCombinedLoading(false); }
  };

  useEffect(() => {
    if (combined && isSuper) loadCombined();
    // eslint-disable-next-line
  }, [combined, isSuper]);

  const load = async (b = branchId) => {
    if (!b) return;
    setLoading(true);
    try {
      const res = await axios.get(`${inventoryUrl}?branch_id=${b}`);
      if (res.data?.success) setRows(res.data.results || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (isSuper) {
      axios.get(branchesUrl).then((res) => {
        if (res.data?.success) {
          const list = res.data.results || [];
          setBranches(list);
          const initial = me?.branch_id || list[0]?.id;
          setBranchId(initial);
          load(initial);
        }
      }).catch(() => {});
    } else {
      // Non-super still asks /branches so the banner can show the branch name.
      axios.get(branchesUrl).then((res) => {
        if (res.data?.success) setBranches(res.data.results || []);
      }).catch(() => {});
      load(me?.branch_id);
    }
    // eslint-disable-next-line
  }, []);

  const currentBranch = branches.find((b) => Number(b.id) === Number(branchId));

  const filtered = useMemo(() => {
    let list = rows;
    if (filter === 'low') list = list.filter((r) => Number(r.qty_available) > 0 && Number(r.qty_available) <= Number(r.reorder_level || 0));
    if (filter === 'oos') list = list.filter((r) => Number(r.qty_available) <= 0);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) => [r.name, r.sku, r.barcode, r.category_name].some((v) => String(v || '').toLowerCase().includes(q)));
    }
    return list;
  }, [rows, search, filter]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [search, filter, pageSize, branchId]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filtered.length);
  const pageRows = filtered.slice(pageStart, pageEnd);

  const counts = useMemo(() => {
    const total = rows.length;
    const low = rows.filter((r) => Number(r.qty_available) > 0 && Number(r.qty_available) <= Number(r.reorder_level || 0)).length;
    const oos = rows.filter((r) => Number(r.qty_available) <= 0).length;
    const healthy = total - low - oos;
    const value = rows.reduce((s, r) => s + Number(r.qty_on_hand) * Number(r.cost_price), 0);
    const retailValue = rows.reduce((s, r) => s + Number(r.qty_on_hand) * Number(r.selling_price), 0);
    const reserved = rows.reduce((s, r) => s + Number(r.qty_reserved), 0);
    return { total, low, oos, healthy, value, retailValue, reserved };
  }, [rows]);

  const loadAnalytics = async (b = branchId) => {
    if (!b) return;
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${inventoryAnalyticsUrl}?branch_id=${b}`);
      if (res.data?.success) setAnalytics(res.data);
    } catch (_) {} finally { setAnalyticsLoading(false); }
  };

  // Lazy-load analytics the first time the user hits the Analytics tab,
  // and refresh whenever the branch changes.
  useEffect(() => {
    if (tab === 'analytics' && branchId) loadAnalytics(branchId);
    // eslint-disable-next-line
  }, [tab, branchId]);

  const loadAdjustments = async (status = adjStatus) => {
    if (!canRequestAdjustment) return;
    setAdjLoading(true);
    try {
      const res = await axios.get(`${inventoryAdjustmentsUrl}?status=${status}`);
      if (res.data?.success) {
        const list = res.data.results || [];
        setAdjustments(list);
        if (status === 'pending') setPendingCount(list.length);
      }
    } catch (err) { showAxiosError(err, 'Could not load adjustments'); }
    finally { setAdjLoading(false); }
  };

  // Keep the pending-count badge fresh whenever the page mounts or branch changes.
  useEffect(() => {
    if (!canRequestAdjustment) return;
    axios.get(`${inventoryAdjustmentsUrl}?status=pending`)
      .then((r) => setPendingCount(r.data?.results?.length || 0))
      .catch(() => {});
    // eslint-disable-next-line
  }, [branchId]);

  // Reload the queue whenever the user switches into the Approvals tab or changes the status filter.
  useEffect(() => {
    if (tab === 'approvals') loadAdjustments(adjStatus);
    // eslint-disable-next-line
  }, [tab, adjStatus]);

  const approveAdj = async (row) => {
    const ok = await swalConfirm(
      'Approve adjustment?',
      `${row.product_name} at ${row.branch_name} — stock will change by ${row.qty_delta > 0 ? '+' : ''}${row.qty_delta}.`,
      { confirmButtonText: 'Approve' }
    );
    if (!ok) return;
    try {
      await axios.post(inventoryAdjustmentApproveUrl(row.id));
      toastSuccess('Adjustment approved');
      await loadAdjustments(adjStatus);
      if (row.branch_id === Number(branchId)) await load();
    } catch (err) { showAxiosError(err, 'Could not approve'); }
  };

  const rejectAdj = async (row) => {
    const Swal = (await import('sweetalert2')).default;
    const { value: reviewNote, isConfirmed } = await Swal.fire({
      title: 'Reject adjustment?',
      input: 'textarea',
      inputLabel: 'Reason (optional, shown to the requester)',
      inputPlaceholder: 'e.g. Please provide a supplier reference first…',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      customClass: {
        popup: 'swal-app', title: 'swal-app-title',
        confirmButton: 'btn-danger swal-app-btn swal-app-danger',
        cancelButton: 'btn-ghost swal-app-btn',
      },
      buttonsStyling: false,
      reverseButtons: true,
    });
    if (!isConfirmed) return;
    try {
      await axios.post(inventoryAdjustmentRejectUrl(row.id), { review_note: reviewNote || null });
      toastSuccess('Adjustment rejected');
      await loadAdjustments(adjStatus);
    } catch (err) { showAxiosError(err, 'Could not reject'); }
  };

  const cancelAdj = async (row) => {
    const ok = await swalConfirm('Cancel this request?', 'The pending adjustment will be withdrawn.', { confirmButtonText: 'Yes, cancel' });
    if (!ok) return;
    try {
      await axios.post(inventoryAdjustmentCancelUrl(row.id));
      toastSuccess('Request cancelled');
      await loadAdjustments(adjStatus);
    } catch (err) { showAxiosError(err, 'Could not cancel'); }
  };

  const openHistory = async (productId) => {
    setHistoryLoading(true);
    setHistory({ loading: true });
    try {
      const res = await axios.get(`${productHistoryUrl(productId)}?branch_id=${branchId}&limit=200`);
      if (res.data?.success) setHistory(res.data);
      else setHistory(null);
    } catch (err) {
      showAxiosError(err, 'Could not load product history');
      setHistory(null);
    } finally { setHistoryLoading(false); }
  };

  const onChangeBranch = (id) => { setBranchId(id); load(id); };

  const openAdjust = (r) => { setAdjust(r); setDelta(''); setReason('adjustment'); setNote(''); setError(null); };
  const bumpDelta = (n) => setDelta(String((Number(delta) || 0) + n));

  // ─── Opening-stock loader helpers ────────────────────────
  const openingItems = useMemo(() => Object.entries(openingQtys)
    .filter(([, v]) => Number(v?.qty) > 0)
    .map(([pid, v]) => ({
      product_id: Number(pid),
      qty: Number(v.qty),
      unit_cost: v.cost !== '' && v.cost !== undefined ? Number(v.cost) : null,
    })), [openingQtys]);

  const openingTotals = useMemo(() => {
    let count = 0, totalQty = 0, totalValue = 0;
    openingItems.forEach((it) => {
      count += 1;
      totalQty += it.qty;
      if (Number.isFinite(it.unit_cost)) totalValue += it.qty * it.unit_cost;
    });
    return { count, totalQty, totalValue };
  }, [openingItems]);

  const filteredForOpening = useMemo(() => {
    if (!openingSearch.trim()) return rows;
    const q = openingSearch.trim().toLowerCase();
    return rows.filter((r) => [r.name, r.sku, r.barcode, r.category_name].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, openingSearch]);

  const setOpeningQty = (productId, qty) => {
    setOpeningQtys((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), qty },
    }));
  };
  const setOpeningCost = (productId, cost) => {
    setOpeningQtys((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), cost },
    }));
  };

  const submitOpeningStock = async () => {
    if (openingItems.length === 0) {
      return swalWarning('Nothing to load', 'Enter a quantity on at least one product.');
    }
    // Warn if any of the selected products already has stock — those will be skipped.
    const alreadyStocked = openingItems.filter((it) => {
      const row = rows.find((r) => r.product_id === it.product_id);
      return row && Number(row.qty_on_hand) > 0;
    });
    const confirmMsg = alreadyStocked.length > 0
      ? `You're about to load ${openingItems.length} product(s). ${alreadyStocked.length} of them already have stock at this branch and will be SKIPPED to protect existing data. Continue?`
      : `Load opening stock for ${openingItems.length} product(s) at ${currentBranch?.name || 'this branch'}?`;
    const ok = await swalDangerConfirm('Load opening stock', confirmMsg, { confirmButtonText: 'Yes, load' });
    if (!ok) return;

    setOpeningSaving(true);
    try {
      const res = await axios.post(openingStockUrl, {
        branch_id: branchId,
        items: openingItems,
        note: openingNote || null,
        replace_existing: openingReplace,
      });
      const d = res.data || {};
      toastSuccess(`Loaded ${d.loaded_count} product(s)${d.skipped_count ? ` · ${d.skipped_count} skipped` : ''}`);
      setOpenOpening(false);
      setOpeningQtys({});
      await load();
    } catch (err) { showAxiosError(err, 'Could not load opening stock'); }
    finally { setOpeningSaving(false); }
  };

  const submitAdjust = async () => {
    setError(null);
    const d = Number(delta);
    if (!d) { setError('Enter a non-zero delta'); return; }
    setSaving(true);
    try {
      await axios.post(inventoryAdjustUrl, { branch_id: branchId, product_id: adjust.product_id, qty_delta: d, reason, note });
      toastSuccess(isSuper
        ? 'Adjustment submitted — approve it in the Approvals tab to apply.'
        : 'Adjustment submitted — awaiting super admin approval.');
      setPendingCount((c) => c + 1);
      setAdjust(null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed';
      setError(msg);
      showAxiosError(err, 'Could not submit adjustment');
    } finally { setSaving(false); }
  };

  // For the per-row stock bar — fills 0–100% based on qty_available vs reorder level.
  // If reorder is 0, scale against the average of all on-hand quantities.
  const stockBarPct = (available, reorder) => {
    if (available <= 0) return 0;
    if (reorder > 0) return Math.min(100, (available / (reorder * 2)) * 100);
    return Math.min(100, (available / 100) * 100);
  };

  return (
    <>
      {/* STATS STRIP — colour-accented analytics */}
      <div className="prod-stats inv-stats">
        <div className="prod-stat tone-brand">
          <div className="ic">📦</div>
          <div className="body">
            <div className="lbl">Tracked SKUs</div>
            <div className="val">{fmtNumber(counts.total)}</div>
            <div className="hint">{counts.healthy} healthy</div>
          </div>
        </div>
        <div className={`prod-stat ${counts.low > 0 ? 'tone-warn' : 'tone-mute'}`}>
          <div className="ic">⚠️</div>
          <div className="body">
            <div className="lbl">Low stock</div>
            <div className="val">{fmtNumber(counts.low)}</div>
            <div className="hint">{counts.low > 0 ? 'reorder soon' : 'all replenished'}</div>
          </div>
        </div>
        <div className={`prod-stat ${counts.oos > 0 ? 'tone-warn' : 'tone-mute'}`}>
          <div className="ic">🛑</div>
          <div className="body">
            <div className="lbl">Out of stock</div>
            <div className="val" style={counts.oos > 0 ? { color: 'var(--danger)' } : null}>{fmtNumber(counts.oos)}</div>
            <div className="hint">{counts.oos > 0 ? 'zero available' : 'fully stocked'}</div>
          </div>
        </div>
        <div className="prod-stat tone-accent">
          <div className="ic">💰</div>
          <div className="body">
            <div className="lbl">Stock value (cost)</div>
            <div className="val">{fmtMoney(counts.value)}</div>
            <div className="hint">retail {fmtMoney(counts.retailValue)}</div>
          </div>
        </div>
        <div className="prod-stat tone-success">
          <div className="ic">🔒</div>
          <div className="body">
            <div className="lbl">Reserved</div>
            <div className="val">{fmtNumber(counts.reserved)}</div>
            <div className="hint">pending sales / transfers</div>
          </div>
        </div>
      </div>

      {/* BRANCH PICKER — chip bar for super admin, banner for others */}
      {isSuper && branches.length > 0 && (
        <div className="ov-branches-chips" style={{ paddingBottom: 12 }}>
          <button
            className={`ov-chip combined ${combined ? 'active' : ''}`}
            onClick={() => setCombined(true)}
            title="Combined view — every product across all branches"
          >
            <span className="ic">🌐</span>
            All branches
            <span className="hq-tag" style={{ background: 'var(--accent)' }}>COMBINED</span>
          </button>
          {branches.map((b) => (
            <button
              key={b.id}
              className={`ov-chip ${!combined && Number(branchId) === b.id ? 'active' : ''}`}
              onClick={() => { setCombined(false); onChangeBranch(b.id); }}
              title={`Switch inventory view to ${b.name}`}
            >
              <span className="ic">{b.is_head_office ? '🏛️' : '🏬'}</span>
              {b.name}
              {b.is_head_office ? <span className="hq-tag">HQ</span> : null}
            </button>
          ))}
        </div>
      )}
      {!isSuper && currentBranch && (
        <div className="audit-scope-banner" style={{ marginBottom: 14 }}>
          <span className="ic">🏬</span>
          <div>
            <strong>Showing inventory for {currentBranch.name}.</strong>
            <p>Stock levels, adjustments and reorder alerts are scoped to your branch. Branches do not share inventory.</p>
          </div>
        </div>
      )}

      {/* Combined (all-branches) view — super admin only */}
      {combined && isSuper && (
        <CombinedInventoryPanel
          rows={combinedRows}
          branches={combinedBranches}
          loading={combinedLoading}
          error={combinedError}
        />
      )}

      {/* Top-level tabs — Stock list vs Analytics charts */}
      {!combined && (
      <>
      <div className="inv-tabs">
        <button className={`inv-tab ${tab === 'stock' ? 'active' : ''}`} onClick={() => setTab('stock')}>
          <span className="ic">📋</span> Stock list
          <span className="ct">{counts.total}</span>
        </button>
        <button className={`inv-tab ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>
          <span className="ic">📊</span> Analytics
        </button>
        {canRequestAdjustment && (
          <button className={`inv-tab ${tab === 'approvals' ? 'active' : ''}`} onClick={() => setTab('approvals')}>
            <span className="ic">🧾</span> {isSuper ? 'Approvals' : 'My requests'}
            {pendingCount > 0 && <span className="ct pending">{pendingCount}</span>}
          </button>
        )}
      </div>

      <div className="panel">
        <div className="panel-head">
          <div>
            <h2>{tab === 'stock' ? 'Inventory' : tab === 'analytics' ? 'Inventory analytics' : (isSuper ? 'Adjustment approvals' : 'My adjustment requests')}</h2>
            <p className="muted-sm" style={{ margin: '2px 0 0' }}>
              {tab === 'stock'
                ? `Live on-hand counts, reorder alerts and quick adjustments for ${currentBranch?.name || 'this branch'}.`
                : tab === 'analytics'
                  ? `Visual breakdown of stock health, value and movement for ${currentBranch?.name || 'this branch'} over the last 30 days.`
                  : isSuper
                    ? 'Review stock adjustments submitted by branch managers. Approve to move stock; reject to send it back.'
                    : 'Adjustments you have submitted for approval. You can withdraw a request while it is still pending.'}
            </p>
          </div>
          {tab === 'stock' && (
            <div className="panel-toolbar">
              <div className="inv-filter-pills">
                {[
                  { k: 'all', l: `All`,        ct: counts.total },
                  { k: 'low', l: `Low`,        ct: counts.low },
                  { k: 'oos', l: `Out`,        ct: counts.oos },
                ].map(({ k, l, ct }) => (
                  <button
                    key={k}
                    onClick={() => setFilter(k)}
                    className={`inv-filter-pill ${filter === k ? 'active' : ''} ${k === 'low' ? 'warn' : ''} ${k === 'oos' ? 'danger' : ''}`}
                  >
                    {l} <span className="ct">{ct}</span>
                  </button>
                ))}
              </div>
              <div className="prod-search">
                <span className="ic">🔍</span>
                <input
                  className="f-input"
                  placeholder="Search name, SKU, category…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button
                className="btn-ghost"
                onClick={() => { setOpeningQtys({}); setOpeningSearch(''); setOpeningNote(''); setOpenOpening(true); }}
                title="Load the physical stock the branch already has (one-time setup)"
              >📥 Load opening stock</button>
            </div>
          )}
        </div>

        {tab === 'stock' && (loading ? (
          <p style={{ color: 'var(--mute)', padding: '24px 0', textAlign: 'center' }}>Loading inventory…</p>
        ) : (
          <>
            {filtered.length > 0 && (
              <div className="prod-pager">
                <div className="pager-info">
                  Showing <strong>{pageStart + 1}–{pageEnd}</strong> of <strong>{filtered.length}</strong>
                  {filter !== 'all' && <span className="muted"> (filtered from {rows.length})</span>}
                </div>
                <div className="pager-controls">
                  <select className="f-select pager-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                    {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} per page</option>)}
                  </select>
                  <div className="pager-nav">
                    <button className="pager-btn" onClick={() => setPage(1)} disabled={currentPage === 1} title="First">«</button>
                    <button className="pager-btn" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} title="Previous">‹</button>
                    <span className="pager-page">{currentPage} / {pageCount}</span>
                    <button className="pager-btn" onClick={() => setPage(currentPage + 1)} disabled={currentPage === pageCount} title="Next">›</button>
                    <button className="pager-btn" onClick={() => setPage(pageCount)} disabled={currentPage === pageCount} title="Last">»</button>
                  </div>
                </div>
              </div>
            )}

            <div className="tbl-wrap">
              <table className="tbl prod-tbl compact">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th className="right" title="What the shop paid per unit (cost / purchase price)">Cost</th>
                    <th className="right" title="Effective markup on cost. Chain: branch+category override → branch default → category default.">Markup</th>
                    <th className="right" title="Effective selling price = cost × (1 + markup). Falls back to stored price if no markup is set.">Sell price</th>
                    <th className="right" title="Selling price − cost, per unit">Profit / unit</th>
                    <th className="right">On hand</th>
                    <th className="right" title="Earmarked for pending outbound transfers">Reserved</th>
                    <th className="right">Available</th>
                    <th className="right" title="Dispatched to/from this branch, not yet received">In transit</th>
                    <th>Stock level</th>
                    <th className="right">Value</th>
                    <th className="right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => {
                    const avail = Number(r.qty_available);
                    const onHand = Number(r.qty_on_hand);
                    const reorder = Number(r.reorder_level || 0);
                    const status = avail <= 0 ? 'oos' : (avail <= reorder ? 'low' : 'ok');
                    const cost = Number(r.cost_price) || 0;
                    const sellEffective = Number(r.effective_price) || Number(r.selling_price) || 0;
                    const isManualOverride = !!Number(r.use_manual_price);
                    const markupPct = r.effective_markup_percent != null ? Number(r.effective_markup_percent) : null;
                    // Which layer of the chain actually supplied the number the user is seeing?
                    const markupSource = isManualOverride ? 'override'
                      : r.branch_markup_percent != null ? 'branch+cat'
                      : r.branch_default_markup_percent != null ? 'branch'
                      : r.default_markup_percent != null ? 'category'
                      : 'manual';
                    const profitPerUnit = sellEffective - cost;
                    const value = onHand * cost;
                    return (
                      <tr key={r.product_id} className={`inv-row inv-row-${status}`}>
                        <td>
                          <div className="prod-cell">
                            <img
                              src={inventoryImageSrc(r)}
                              alt=""
                              className="prod-thumb"
                              onError={(e) => { e.target.src = `${ASSETS}/uploads/products/defaults/generic.svg`; }}
                            />
                            <div className="prod-name-block">
                              <div className="nm">{r.name}</div>
                              <div className="prod-sub">
                                <code className="prod-sku">{r.sku}</code>
                                <code className="prod-unit">{r.unit}</code>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {r.category_name
                            ? <span className="prod-cat-pill">{r.category_name}</span>
                            : <span style={{ color: 'var(--mute-2)', fontSize: 12 }}>—</span>}
                        </td>
                        <td className="right num">{fmtMoney(cost)}</td>
                        <td className="right">
                          {isManualOverride ? (
                            <span
                              className="mk-src mk-src-override"
                              title="This product has 'Use my own selling price' turned on — markup chain is ignored for this item."
                            >Override</span>
                          ) : markupPct != null ? (
                            <div className="mk-cell" title={
                              markupSource === 'branch+cat' ? 'Branch-specific override for this category'
                              : markupSource === 'branch' ? 'Branch default markup (applies to all categories at this branch)'
                              : markupSource === 'category' ? 'Category default markup'
                              : 'Manual price'
                            }>
                              <span className="mk-pct">{markupPct.toFixed(0)}%</span>
                              <span className={`mk-src mk-src-${markupSource.replace('+','-')}`}>
                                {markupSource === 'branch+cat' ? 'Branch·Cat'
                                : markupSource === 'branch' ? 'Branch'
                                : markupSource === 'category' ? 'Category'
                                : 'Manual'}
                              </span>
                            </div>
                          ) : <span style={{ color: 'var(--mute-2)' }}>—</span>}
                        </td>
                        <td className="right num"><strong>{fmtMoney(sellEffective)}</strong></td>
                        <td className="right num">
                          {cost > 0 && sellEffective > 0 ? (
                            <span className={profitPerUnit > 0 ? 'inv-ok' : profitPerUnit < 0 ? 'inv-danger' : ''}>
                              {fmtMoney(profitPerUnit)}
                            </span>
                          ) : <span style={{ color: 'var(--mute-2)' }}>—</span>}
                        </td>
                        <td className="right num">{fmtNumber(onHand)}</td>
                        <td className="right num">
                          {Number(r.qty_reserved) > 0
                            ? <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{fmtNumber(r.qty_reserved)}</span>
                            : <span style={{ color: 'var(--mute-2)' }}>0</span>}
                        </td>
                        <td className="right num">
                          <strong className={status === 'oos' ? 'inv-danger' : status === 'low' ? 'inv-warn' : ''}>
                            {fmtNumber(avail)}
                          </strong>
                        </td>
                        <td className="right">
                          {Number(r.qty_in_transit_out) > 0 && (
                            <div className="transit-chip out" title="Dispatched from here, not yet received elsewhere">
                              🚚 {fmtNumber(r.qty_in_transit_out)} out
                            </div>
                          )}
                          {Number(r.qty_incoming) > 0 && (
                            <div className="transit-chip in" title="Dispatched to here from another branch">
                              📥 {fmtNumber(r.qty_incoming)} in
                            </div>
                          )}
                          {Number(r.qty_in_transit_out) === 0 && Number(r.qty_incoming) === 0 && (
                            <span style={{ color: 'var(--mute-2)' }}>—</span>
                          )}
                        </td>
                        <td>
                          <div className="inv-bar-cell">
                            <div className={`inv-bar inv-bar-${status}`}>
                              <div className="fill" style={{ width: `${stockBarPct(avail, reorder)}%` }} />
                              {reorder > 0 && (
                                <div className="reorder-mark" style={{ left: `${Math.min(100, (reorder / Math.max(reorder * 2, 1)) * 100)}%` }} title={`Reorder at ${reorder}`} />
                              )}
                            </div>
                            <span className={`inv-status-badge ${status}`}>
                              {status === 'oos' ? 'Out' : status === 'low' ? 'Low' : 'OK'}
                            </span>
                          </div>
                        </td>
                        <td className="right num">{fmtMoney(value)}</td>
                        <td className="right">
                          <div className="row-icons">
                            <button
                              className="icon-btn"
                              onClick={() => openHistory(r.product_id)}
                              title="View stock history"
                              aria-label="View history"
                            >
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="10" cy="10" r="7"/>
                                <path d="M10 6v4l3 2"/>
                              </svg>
                            </button>
                            {canRequestAdjustment && (
                              <button
                                className="icon-btn brand"
                                onClick={() => openAdjust(r)}
                                title="Request stock adjustment"
                                aria-label="Request stock adjustment"
                              >
                                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M10 4v12M4 10h12"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan="13" className="prod-empty-cell">
                      {rows.length === 0
                        ? 'No products at this branch yet. Add products + opening stock, or receive a purchase first.'
                        : 'No items match the current filter.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {filtered.length > 0 && (() => {
              const costVal   = filtered.reduce((s, r) => s + Number(r.qty_on_hand) * (Number(r.cost_price) || 0), 0);
              const retailVal = filtered.reduce((s, r) => s + Number(r.qty_on_hand) * (Number(r.effective_price) || Number(r.selling_price) || 0), 0);
              const potential = retailVal - costVal;
              const marginPct = retailVal > 0 ? (potential / retailVal) * 100 : 0;
              return (
                <div className="prod-analytics">
                  <div className="analytics-card">
                    <div className="lbl">Items shown</div>
                    <div className="val">{filtered.length}</div>
                  </div>
                  <div className="analytics-card">
                    <div className="lbl">Cost value</div>
                    <div className="val">{fmtMoney(costVal)}</div>
                    <div className="sub">on-hand × cost</div>
                  </div>
                  <div className="analytics-card">
                    <div className="lbl">Retail value</div>
                    <div className="val brand">{fmtMoney(retailVal)}</div>
                    <div className="sub">on-hand × effective price</div>
                  </div>
                  <div className="analytics-card">
                    <div className="lbl">Potential margin</div>
                    <div className="val" style={{ color: 'var(--success)' }}>{fmtMoney(potential)}</div>
                    <div className="sub">{marginPct.toFixed(1)}% of retail</div>
                  </div>
                  <div className="analytics-card">
                    <div className="lbl">Page</div>
                    <div className="val">{currentPage} of {pageCount}</div>
                  </div>
                </div>
              );
            })()}
          </>
        ))}

        {/* ANALYTICS TAB — charts + insights */}
        {tab === 'analytics' && (
          analyticsLoading || !analytics ? (
            <p style={{ color: 'var(--mute)', padding: '24px 0', textAlign: 'center' }}>Loading analytics…</p>
          ) : (
            <div className="inv-charts">
              {/* Stock health donut */}
              <div className="chart-card">
                <div className="chart-head">
                  <h3>Stock health</h3>
                  <p>How many SKUs are healthy, low or out of stock right now.</p>
                </div>
                <div className="chart-body">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Healthy', value: analytics.status.ok, color: 'var(--success)' },
                          { name: 'Low stock', value: analytics.status.low, color: 'var(--warn)' },
                          { name: 'Out of stock', value: analytics.status.oos, color: 'var(--danger)' },
                        ].filter((d) => d.value > 0)}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={3}
                      >
                        {[
                          { color: 'var(--success)' },
                          { color: 'var(--warn)' },
                          { color: 'var(--danger)' },
                        ].map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} stroke="var(--surface)" strokeWidth={2} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, fontSize: 13 }}
                        formatter={(v, n) => [`${v} SKUs`, n]}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        iconType="circle"
                        wrapperStyle={{ fontSize: 12, color: 'var(--mute)' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="chart-stats">
                  <div><span className="dot ok"/>Healthy <strong>{analytics.status.ok}</strong></div>
                  <div><span className="dot low"/>Low <strong>{analytics.status.low}</strong></div>
                  <div><span className="dot oos"/>Out <strong>{analytics.status.oos}</strong></div>
                </div>
              </div>

              {/* Value by category */}
              <div className="chart-card wide">
                <div className="chart-head">
                  <h3>Stock value by category</h3>
                  <p>Where your inventory cash is tied up.</p>
                </div>
                <div className="chart-body">
                  {analytics.by_category.length === 0 ? (
                    <p className="chart-empty">No stock-on-hand yet. Run a purchase or set opening stock.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(220, analytics.by_category.length * 36)}>
                      <BarChart
                        data={analytics.by_category}
                        layout="vertical"
                        margin={{ top: 8, right: 30, left: 12, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                        <XAxis type="number" stroke="var(--mute)" fontSize={11} tickFormatter={(v) => `GHS ${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}`} />
                        <YAxis type="category" dataKey="name" stroke="var(--mute)" fontSize={12} width={120} />
                        <Tooltip
                          contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, fontSize: 13 }}
                          formatter={(v, k) => k === 'cost_value' ? [`GHS ${Number(v).toFixed(2)}`, 'Cost value'] : [`GHS ${Number(v).toFixed(2)}`, 'Retail value']}
                          labelStyle={{ color: 'var(--ink)', fontWeight: 700 }}
                        />
                        <Bar dataKey="cost_value" fill="var(--brand)" radius={[0, 6, 6, 0]} name="Cost value" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* 30-day movement trend */}
              <div className="chart-card wide">
                <div className="chart-head">
                  <h3>Stock movement — last 30 days</h3>
                  <p>Daily inflow (purchases, adjustments) vs outflow (sales, returns).</p>
                </div>
                <div className="chart-body">
                  {analytics.trend.length === 0 ? (
                    <p className="chart-empty">No movement recorded in the last 30 days.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={analytics.trend.map((t) => ({ ...t, day: String(t.day).slice(5, 10) }))} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                        <defs>
                          <linearGradient id="inflowGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--success)" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="var(--success)" stopOpacity={0.02}/>
                          </linearGradient>
                          <linearGradient id="outflowGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--danger)" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="var(--danger)" stopOpacity={0.02}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                        <XAxis dataKey="day" stroke="var(--mute)" fontSize={11} />
                        <YAxis stroke="var(--mute)" fontSize={11} />
                        <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, fontSize: 13 }} />
                        <Area type="monotone" dataKey="inflow" stroke="var(--success)" strokeWidth={2} fill="url(#inflowGrad)" name="Inflow" />
                        <Area type="monotone" dataKey="outflow" stroke="var(--danger)" strokeWidth={2} fill="url(#outflowGrad)" name="Outflow" />
                        <Legend wrapperStyle={{ fontSize: 12, color: 'var(--mute)' }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Top items by value */}
              <div className="chart-card">
                <div className="chart-head">
                  <h3>Top 10 items by stock value</h3>
                  <p>Where the most cash is sitting.</p>
                </div>
                <div className="top-list">
                  {analytics.top_value.length === 0 ? (
                    <p className="chart-empty">No stock-on-hand yet.</p>
                  ) : analytics.top_value.map((p, i) => {
                    const total = analytics.top_value[0].value || 1;
                    const pct = (Number(p.value) / Number(total)) * 100;
                    return (
                      <button key={p.id} className="top-row" onClick={() => openHistory(p.id)} title="View product history">
                        <span className="rank">{i + 1}</span>
                        <img src={`${ASSETS}${p.image || defaultImageFor(p)}`} alt="" />
                        <div className="info">
                          <div className="nm">{p.name}</div>
                          <div className="sub">{fmtNumber(p.qty_on_hand)} {p.unit} · {p.category_name || 'Uncat'}</div>
                          <div className="bar"><div className="fill" style={{ width: `${pct}%` }}/></div>
                        </div>
                        <div className="value">{fmtMoney(p.value)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Top movers (last 30 days) */}
              <div className="chart-card">
                <div className="chart-head">
                  <h3>Most-moved (last 30 days)</h3>
                  <p>Items with the highest in/out volume — click to trace.</p>
                </div>
                <div className="top-list">
                  {analytics.top_movers.length === 0 ? (
                    <p className="chart-empty">No movement in the last 30 days.</p>
                  ) : analytics.top_movers.map((p, i) => {
                    const max = analytics.top_movers[0].total_moved || 1;
                    const inPct = (Number(p.inflow) / Number(max)) * 100;
                    const outPct = (Number(p.outflow) / Number(max)) * 100;
                    return (
                      <button key={p.id} className="top-row" onClick={() => openHistory(p.id)} title="View product history">
                        <span className="rank">{i + 1}</span>
                        <img src={`${ASSETS}${p.image || defaultImageFor(p)}`} alt="" />
                        <div className="info">
                          <div className="nm">{p.name}</div>
                          <div className="sub">{p.move_count} moves · in {fmtNumber(p.inflow)} · out {fmtNumber(p.outflow)}</div>
                          <div className="bar split">
                            <div className="fill in" style={{ width: `${inPct}%` }}/>
                            <div className="fill out" style={{ width: `${outPct}%` }}/>
                          </div>
                        </div>
                        <div className="value">{fmtNumber(p.total_moved)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )
        )}

        {/* APPROVALS TAB — pending / approved / rejected requests */}
        {tab === 'approvals' && (
          <>
            <div className="adj-status-tabs">
              {[
                { k: 'pending',  l: 'Pending' },
                { k: 'approved', l: 'Approved' },
                { k: 'rejected', l: 'Rejected' },
                { k: 'all',      l: 'All' },
              ].map((s) => (
                <button
                  key={s.k}
                  className={`adj-chip ${adjStatus === s.k ? 'active' : ''} ${s.k}`}
                  onClick={() => setAdjStatus(s.k)}
                >{s.l}</button>
              ))}
              <div className="grow" />
              <button className="btn-ghost" onClick={() => loadAdjustments(adjStatus)}>Refresh</button>
            </div>
            {adjLoading ? (
              <p style={{ color: 'var(--mute)', padding: '24px 0', textAlign: 'center' }}>Loading requests…</p>
            ) : adjustments.length === 0 ? (
              <p style={{ color: 'var(--mute)', padding: '24px 0', textAlign: 'center' }}>
                No {adjStatus === 'all' ? '' : adjStatus} adjustments to show.
              </p>
            ) : (
              <div className="adj-table-wrap">
                <table className="adj-table">
                  <thead>
                    <tr>
                      <th>Requested</th>
                      <th>Product</th>
                      {isSuper && <th>Branch</th>}
                      <th className="right">Delta</th>
                      <th>Reason</th>
                      <th>Requested by</th>
                      <th>Status</th>
                      <th className="right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjustments.map((a) => {
                      const isPending = a.status === 'pending';
                      const canCancel = isPending && (isSuper || a.requested_by === me?.id);
                      return (
                        <tr key={a.id}>
                          <td>{fmtDate(a.requested_at)}</td>
                          <td>
                            <div className="adj-prod-cell">
                              <span className="nm">{a.product_name}</span>
                              <code className="prod-sku">{a.product_sku}</code>
                            </div>
                            {a.note && <div className="adj-note">“{a.note}”</div>}
                          </td>
                          {isSuper && <td>{a.branch_name}</td>}
                          <td className={`right adj-delta ${a.qty_delta > 0 ? 'up' : 'down'}`}>
                            {a.qty_delta > 0 ? `+${a.qty_delta}` : a.qty_delta}
                          </td>
                          <td>{a.reason}</td>
                          <td>{a.requested_by_name}</td>
                          <td>
                            <span className={`adj-status-badge ${a.status}`}>{a.status}</span>
                            {a.review_note && <div className="adj-note">{a.review_note}</div>}
                          </td>
                          <td className="right">
                            <div className="row-icons">
                              {isSuper && isPending && (
                                <>
                                  <button className="icon-btn success" onClick={() => approveAdj(a)} title="Approve">✓</button>
                                  <button className="icon-btn danger" onClick={() => rejectAdj(a)} title="Reject">✕</button>
                                </>
                              )}
                              {canCancel && !isSuper && (
                                <button className="icon-btn danger" onClick={() => cancelAdj(a)} title="Withdraw request">↺</button>
                              )}
                              {!isPending && <span className="muted-sm">—</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
      </>
      )}

      {/* Stock adjustment dialog */}
      <Modal
        title={adjust ? `Request stock adjustment — ${adjust.name}` : 'Request stock adjustment'}
        open={!!adjust} onClose={() => setAdjust(null)} width={560}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setAdjust(null)}>Cancel</button>
            <button className="btn-primary" onClick={submitAdjust} disabled={saving || !delta}>
              {saving ? 'Sending…' : 'Submit for approval'}
            </button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}
        {adjust && (
          <div className="notice info" style={{ marginBottom: 12 }}>
            {isSuper
              ? 'This request will be added to the approvals queue. Stock will not change until you approve it there.'
              : 'Your request will be sent to the super administrator for approval. Stock is not moved until it is approved.'}
          </div>
        )}
        {adjust && (
          <>
            <div className="inv-adjust-summary">
              <img
                src={inventoryImageSrc(adjust)}
                alt=""
                onError={(e) => { e.target.src = `${ASSETS}/uploads/products/defaults/generic.svg`; }}
              />
              <div>
                <div className="adjust-name">{adjust.name}</div>
                <div className="adjust-meta">
                  <code className="prod-sku">{adjust.sku}</code> · {adjust.unit}
                </div>
                <div className="adjust-current">
                  <div><span className="lbl">On hand</span><span className="val">{fmtNumber(adjust.qty_on_hand)}</span></div>
                  <div><span className="lbl">Reserved</span><span className="val">{fmtNumber(adjust.qty_reserved)}</span></div>
                  <div><span className="lbl">Available</span><span className="val">{fmtNumber(adjust.qty_available)}</span></div>
                </div>
              </div>
            </div>

            <div className="f-row" style={{ marginTop: 18 }}>
              <label className="f-label">Quick adjust</label>
              <div className="quick-delta-row">
                {QUICK_DELTAS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`quick-delta ${n < 0 ? 'down' : 'up'}`}
                    onClick={() => bumpDelta(n)}
                  >{n > 0 ? `+${n}` : n}</button>
                ))}
                <button type="button" className="quick-delta clear" onClick={() => setDelta('')}>Clear</button>
              </div>
            </div>

            <div className="f-grid f-grid-2">
              <div className="f-row">
                <label className="f-label">Delta (− to decrease) *</label>
                <input
                  className="f-input"
                  type="number"
                  step="0.001"
                  value={delta}
                  onChange={(e) => setDelta(e.target.value)}
                  placeholder="e.g. -3 or 10"
                />
              </div>
              <div className="f-row">
                <label className="f-label">Reason</label>
                <select className="f-select" value={reason} onChange={(e) => setReason(e.target.value)}>
                  {REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Note</label>
                <input
                  className="f-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional — e.g. supplier returned 2 cartons"
                />
              </div>
            </div>

            {delta !== '' && Number(delta) !== 0 && (
              <div className="inv-projection">
                After adjustment: <strong>{Number(adjust.qty_on_hand) + Number(delta)} {adjust.unit}</strong> on hand
                {Number(adjust.qty_on_hand) + Number(delta) < 0 && <span className="warn"> · negative stock not allowed</span>}
              </div>
            )}
            {adjust.last_restocked_at && (
              <div className="inv-last-restock">Last restocked {fmtDate(adjust.last_restocked_at)}</div>
            )}
          </>
        )}
      </Modal>

      {/* Per-product trace — full stock movement history */}
      <Modal
        title={history?.product ? `Stock trace — ${history.product.name}` : 'Stock trace'}
        open={!!history} onClose={() => setHistory(null)} width={820}
        footer={<button className="btn-ghost" onClick={() => setHistory(null)}>Close</button>}
      >
        {historyLoading || !history?.product ? (
          <p style={{ color: 'var(--mute)', padding: '24px 0', textAlign: 'center' }}>Loading history…</p>
        ) : (
          <>
            <div className="trace-header">
              <img
                src={`${ASSETS}${history.product.image || defaultImageFor(history.product)}`}
                alt=""
                onError={(e) => { e.target.src = `${ASSETS}/uploads/products/defaults/generic.svg`; }}
              />
              <div className="trace-info">
                <div className="trace-name">{history.product.name}</div>
                <div className="trace-meta">
                  <code className="prod-sku">{history.product.sku}</code>
                  · {history.product.unit}
                  {history.product.category_name && <> · <span className="prod-cat-pill">{history.product.category_name}</span></>}
                </div>
                <div className="trace-stats">
                  <div><span className="lbl">On hand now</span><span className="val">{fmtNumber(history.product.qty_on_hand)}</span></div>
                  <div><span className="lbl">Total in</span><span className="val success">+{fmtNumber(history.totals.totalIn)}</span></div>
                  <div><span className="lbl">Total out</span><span className="val danger">−{fmtNumber(history.totals.totalOut)}</span></div>
                  <div><span className="lbl">Events</span><span className="val">{history.movements.length}</span></div>
                </div>
              </div>
            </div>

            {/* Mini trend chart for this product */}
            {history.movements.length > 1 && (() => {
              // Walk movements oldest-to-newest, computing running balance.
              const sorted = [...history.movements].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
              const start = Number(history.product.qty_on_hand) - history.movements.reduce((s, m) => s + Number(m.qty_delta), 0);
              let running = start;
              const data = sorted.map((m) => {
                running += Number(m.qty_delta);
                return { day: new Date(m.created_at).toISOString().slice(5, 10), level: running };
              });
              return (
                <div className="trace-chart">
                  <ResponsiveContainer width="100%" height={120}>
                    <AreaChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="traceGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--brand)" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="var(--brand)" stopOpacity={0.02}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="day" stroke="var(--mute)" fontSize={10} />
                      <YAxis stroke="var(--mute)" fontSize={10} />
                      <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, fontSize: 12 }} />
                      <Area type="monotone" dataKey="level" stroke="var(--brand)" strokeWidth={2} fill="url(#traceGrad)" name="Stock level" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* Timeline — newest first, with vertical connector */}
            <h4 className="trace-section-head">Movement timeline</h4>
            {history.movements.length === 0 ? (
              <p style={{ color: 'var(--mute)', textAlign: 'center', padding: '20px 0' }}>
                No movements recorded yet for this product.
              </p>
            ) : (
              <ul className="trace-timeline">
                {history.movements.map((m) => {
                  const d = Number(m.qty_delta);
                  const sign = d >= 0 ? 'in' : 'out';
                  const icon = m.ref_type === 'sale' ? '🛒'
                    : m.ref_type === 'purchase' ? '📥'
                    : m.ref_type === 'opening' || m.ref_type === 'starter_catalog' ? '🎁'
                    : m.reason === 'damage' ? '💥'
                    : m.reason === 'theft' ? '⚠️'
                    : m.reason === 'count_correction' ? '🔢'
                    : m.reason === 'returned' ? '↩️'
                    : '✏️';
                  return (
                    <li key={m.id} className={`trace-event ${sign}`}>
                      <div className="trace-dot">{icon}</div>
                      <div className="trace-card">
                        <div className="trace-card-head">
                          <span className={`trace-delta ${sign}`}>
                            {d > 0 ? '+' : ''}{fmtNumber(d)} {history.product.unit}
                          </span>
                          <span className="trace-reason">{String(m.reason || '').replace('_', ' ')}</span>
                          {m.ref_label && (
                            <span className="trace-ref">
                              {m.ref_type === 'sale' ? 'Receipt' : m.ref_type === 'purchase' ? 'Purchase' : m.ref_type}
                              {' '}
                              <code>{m.ref_label}</code>
                            </span>
                          )}
                        </div>
                        <div className="trace-card-foot">
                          <span className="actor">
                            {m.actor_name || 'System'}
                            {m.actor_role && <span className="role"> · {String(m.actor_role).replace('_', ' ')}</span>}
                          </span>
                          <span className="ts">{fmtDate(m.created_at)}</span>
                        </div>
                        {m.note && <div className="trace-note">"{m.note}"</div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </Modal>

      {/* ─── Opening stock loader ─── */}
      <Modal
        title="Load opening stock"
        open={openOpening}
        onClose={() => setOpenOpening(false)}
        width={1080}
        footer={
          <>
            <div className="opening-footer-summary">
              <div><span className="lbl">Items</span><strong>{openingTotals.count}</strong></div>
              <div><span className="lbl">Total qty</span><strong>{fmtNumber(openingTotals.totalQty)}</strong></div>
              <div><span className="lbl">Est. value</span><strong>{fmtMoney(openingTotals.totalValue)}</strong></div>
            </div>
            <button className="btn-ghost" onClick={() => setOpenOpening(false)}>Cancel</button>
            <button
              className="btn-primary"
              onClick={submitOpeningStock}
              disabled={openingSaving || openingTotals.count === 0}
            >
              {openingSaving ? 'Loading…' : `Load ${openingTotals.count || ''} item${openingTotals.count === 1 ? '' : 's'}`}
            </button>
          </>
        }
      >
        <div className="opening-intro">
          <span className="ic">💡</span>
          <div>
            <strong>Register stock the branch already has.</strong>
            <p>
              Enter the physical quantities your <strong>{currentBranch?.name || 'branch'}</strong> is currently holding — this is a one-time load for new branches joining the system.
              Any product that already has recorded stock will be <strong>skipped</strong> to protect existing data (use Adjust for corrections).
              Every entry writes a proper <code>opening_stock</code> movement to the audit trail.
            </p>
          </div>
        </div>

        <div className="opening-toolbar">
          <div className="prod-search" style={{ flex: 1, maxWidth: 320 }}>
            <span className="ic">🔍</span>
            <input
              className="f-input"
              placeholder="Search product, SKU, category…"
              value={openingSearch}
              onChange={(e) => setOpeningSearch(e.target.value)}
            />
          </div>
          <input
            className="f-input"
            placeholder="Note (e.g. 'From physical count 2026-07-01')"
            value={openingNote}
            onChange={(e) => setOpeningNote(e.target.value)}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <label className={`opening-replace-toggle ${openingReplace ? 'on' : ''}`}>
            <input
              type="checkbox"
              checked={openingReplace}
              onChange={(e) => setOpeningReplace(e.target.checked)}
            />
            <span>
              <strong>Replace existing stock</strong>
              <small>Override the safety guard for branches with starter/sample stock</small>
            </span>
          </label>
        </div>

        <div className="opening-table-wrap">
          <table className="tbl prod-tbl compact opening-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}></th>
                <th>Product</th>
                <th className="right">Current on hand</th>
                <th className="right" style={{ width: 130 }}>Opening qty</th>
                <th className="right" style={{ width: 130 }}>Unit cost</th>
                <th className="right">Line value</th>
              </tr>
            </thead>
            <tbody>
              {filteredForOpening.map((r) => {
                const v = openingQtys[r.product_id] || {};
                const qty = Number(v.qty) || 0;
                const cost = v.cost !== '' && v.cost !== undefined ? Number(v.cost) : Number(r.cost_price);
                const line = qty * (Number.isFinite(cost) ? cost : 0);
                const hasExisting = Number(r.qty_on_hand) > 0;
                // If the branch already has stock and the user hasn't ticked
                // "replace existing", the row will be skipped server-side. We
                // still allow typing so the user isn't visually blocked.
                const willSkip = hasExisting && !openingReplace;
                return (
                  <tr key={r.product_id} className={qty > 0 ? 'opening-row-active' : ''}>
                    <td>
                      <img
                        src={inventoryImageSrc(r)}
                        alt=""
                        className="prod-thumb"
                        onError={(e) => { e.target.src = `${ASSETS}/uploads/products/defaults/generic.svg`; }}
                        style={{ width: 34, height: 34 }}
                      />
                    </td>
                    <td>
                      <div className="nm">{r.name}</div>
                      <div className="prod-sub">
                        <code className="prod-sku">{r.sku}</code>
                        {r.category_name && <span className="prod-brand-inline">· {r.category_name}</span>}
                      </div>
                    </td>
                    <td className="right num">
                      {hasExisting ? (
                        <span className={willSkip ? 'opening-skip-tag' : 'opening-existing-tag'}
                              title={willSkip
                                ? 'Will be skipped — already has stock. Tick "Replace existing" to override.'
                                : 'Existing stock will be replaced by the value you enter.'}>
                          {fmtNumber(r.qty_on_hand)} {r.unit || ''}
                        </span>
                      ) : <span style={{ color: 'var(--mute-2)' }}>0</span>}
                    </td>
                    <td className="right">
                      <input
                        type="number" min="0" step="0.001"
                        className="opening-input"
                        value={v.qty || ''}
                        onChange={(e) => setOpeningQty(r.product_id, e.target.value)}
                        placeholder="0"
                      />
                    </td>
                    <td className="right">
                      <input
                        type="number" min="0" step="0.01"
                        className="opening-input"
                        value={v.cost !== undefined ? v.cost : String(r.cost_price)}
                        onChange={(e) => setOpeningCost(r.product_id, e.target.value)}
                        placeholder="0.00"
                      />
                    </td>
                    <td className="right num">
                      {qty > 0
                        ? (willSkip
                            ? <span title="Will be skipped" style={{ color: 'var(--warn)' }}>{fmtMoney(line)}</span>
                            : fmtMoney(line))
                        : <span style={{ color: 'var(--mute-2)' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
              {filteredForOpening.length === 0 && (
                <tr><td colSpan="6" className="prod-empty-cell">
                  {rows.length === 0
                    ? 'No products yet. Add products from the Products page first.'
                    : 'No products match that search.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
};

// ─── Combined inventory (all branches, super-admin view) ─────────────
// Wide-form matrix: rows = products, columns = branches, plus rollup totals.
const CombinedInventoryPanel = ({ rows, branches, loading, error }) => {
  const [search, setSearch] = React.useState('');
  const [sort, setSort]     = React.useState('name'); // name | on_hand | available | value

  // Index by (product_id, branch_id) for O(1) cell lookup
  const perBranch = React.useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const byB = new Map();
      for (const b of (r.branches || [])) byB.set(b.branch_id, b);
      m.set(r.product_id, byB);
    }
    return m;
  }, [rows]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = list.filter((r) => [r.name, r.sku, r.category_name].some((v) => String(v || '').toLowerCase().includes(q)));
    }
    switch (sort) {
      case 'on_hand':   list = [...list].sort((a, b) => Number(b.total_on_hand)   - Number(a.total_on_hand)); break;
      case 'available': list = [...list].sort((a, b) => Number(b.total_available) - Number(a.total_available)); break;
      case 'value':     list = [...list].sort((a, b) => Number(b.total_cost_value)- Number(a.total_cost_value)); break;
      default:          list = [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }
    return list;
  }, [rows, search, sort]);

  const totals = React.useMemo(() => {
    let on = 0, avail = 0, val = 0, oos = 0;
    for (const r of rows) {
      on    += Number(r.total_on_hand)    || 0;
      avail += Number(r.total_available)  || 0;
      val   += Number(r.total_cost_value) || 0;
      if (Number(r.total_available) <= 0) oos += 1;
    }
    return { on, avail, val, oos, products: rows.length };
  }, [rows]);

  return (
    <div className="panel">
      <div className="panel-head">
        <div className="head-text">
          <h2>Company-wide inventory</h2>
          <span className="sub">
            Each row is a product; each branch has its own column. Scroll sideways if you have many branches.
          </span>
        </div>
        <div className="head-actions">
          <div className="prod-search">
            <span className="ic">🔍</span>
            <input
              className="f-input"
              placeholder="Search name, SKU, category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="f-select" style={{ maxWidth: 210 }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="name">Sort: Name (A–Z)</option>
            <option value="on_hand">Sort: On hand (high → low)</option>
            <option value="available">Sort: Available (high → low)</option>
            <option value="value">Sort: Stock value (high → low)</option>
          </select>
        </div>
      </div>

      <div className="cinv-kpis">
        <div className="cinv-kpi"><span className="lbl">Products</span><span className="val">{fmtNumber(totals.products)}</span></div>
        <div className="cinv-kpi"><span className="lbl">Total units on hand</span><span className="val">{fmtNumber(totals.on)}</span></div>
        <div className="cinv-kpi"><span className="lbl">Total available</span><span className="val">{fmtNumber(totals.avail)}</span></div>
        <div className="cinv-kpi"><span className="lbl">Company stock value</span><span className="val">{fmtMoney(totals.val)}</span></div>
        <div className="cinv-kpi warn"><span className="lbl">Out everywhere</span><span className="val">{fmtNumber(totals.oos)}</span></div>
      </div>

      {error ? (
        <div className="notice">
          <strong>Couldn't load combined view.</strong>&nbsp;{error}
        </div>
      ) : loading ? (
        <div className="empty-state"><div className="ic">⏳</div><h4>Rolling up every branch…</h4></div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <div className="ic">📦</div>
          <h4>No products in the catalog yet</h4>
          <p>Once products are added and stocked at any branch, the combined view will populate.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="ic">🔍</div>
          <h4>No products match “{search}”.</h4>
        </div>
      ) : (
        <div className="tbl-wrap cinv-wrap">
          <table className="tbl cinv-matrix">
            <thead>
              <tr>
                <th className="cinv-sticky-l">Product</th>
                <th>Category</th>
                {branches.map((b) => (
                  <th key={b.id} className="right cinv-branch-th">
                    <div className="cinv-branch-th-inner">
                      <span className="ic">{b.is_head_office ? '🏛️' : '🏬'}</span>
                      <span className="nm">{b.name}</span>
                      {b.is_head_office && <span className="hq-tag">HQ</span>}
                    </div>
                  </th>
                ))}
                <th className="right cinv-total-th">On hand</th>
                <th className="right cinv-total-th">Available</th>
                <th className="right cinv-total-th">Value</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const oos = Number(r.total_available) <= 0;
                return (
                  <tr key={r.product_id}>
                    <td className="cinv-sticky-l">
                      <div className="strong">{r.name}</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {r.sku ? `SKU ${r.sku}` : ''}{r.unit ? ` · ${r.unit}` : ''}
                      </div>
                    </td>
                    <td>{r.category_name || <span className="muted">—</span>}</td>
                    {branches.map((b) => {
                      const inv = perBranch.get(r.product_id)?.get(b.id);
                      const on  = Number(inv?.qty_on_hand)         || 0;
                      const res = Number(inv?.qty_reserved)        || 0;
                      const out = Number(inv?.qty_in_transit_out)  || 0;
                      const inc = Number(inv?.qty_incoming)        || 0;
                      const empty = on === 0 && res === 0 && out === 0 && inc === 0;
                      return (
                        <td key={b.id} className={`right num cinv-cell ${empty ? 'zero' : ''}`}>
                          {empty ? (
                            <span className="muted">—</span>
                          ) : (
                            <>
                              <div className={`cinv-cell-qty ${on === 0 ? 'muted' : ''}`}>{fmtNumber(on)}</div>
                              {res > 0 && <div className="cinv-cell-sub">{fmtNumber(res)} reserved</div>}
                              {(out > 0 || inc > 0) && (
                                <div className="cinv-cell-transit">
                                  {out > 0 && <span title={`${fmtNumber(out)} dispatched, not received`}>🚚 {fmtNumber(out)}</span>}
                                  {inc > 0 && <span title={`${fmtNumber(inc)} incoming`}>📥 {fmtNumber(inc)}</span>}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      );
                    })}
                    <td className="right num strong cinv-total-cell">{fmtNumber(r.total_on_hand)}</td>
                    <td className="right num strong cinv-total-cell" style={{ color: oos ? 'var(--danger)' : undefined }}>
                      {fmtNumber(r.total_available)}
                    </td>
                    <td className="right num cinv-total-cell">{fmtMoney(r.total_cost_value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Inventory;
